import { useState } from 'react';
import api from '../../services/api';
import { X } from 'lucide-react';
import WheelPicker from '../shared/WheelPicker';
import { useToast } from '../../context/ToastContext';

export default function CreateModuleModal({ isOpen, onClose, onSuccess, editMode = false, moduleData = null }) {
  const toast = useToast();
  const [formData, setFormData] = useState({
    title_pt: moduleData?.title_pt || '',
    title_en: moduleData?.title_en || '',
    description_pt: moduleData?.description_pt || '',
    description_en: moduleData?.description_en || '',
    thumbnail_url: moduleData?.thumbnail_url || '',
    order_index: moduleData?.order_index || 0,
    is_published: moduleData?.is_published || false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const dataToSend = {
        ...formData,
        title_en: formData.title_pt,
        description_en: formData.description_pt
      };

      let data;
      if (editMode && moduleData) {
        const response = await api.put(`/modules/${moduleData.id}`, dataToSend);
        data = response.data;
        console.log('✅ Módulo atualizado:', data);
        toast.success('Módulo atualizado com sucesso!');
      } else {
        const response = await api.post('/modules', dataToSend);
        data = response.data;
        console.log('✅ Módulo criado:', data);
        toast.success('Módulo criado com sucesso!');
      }
      
      onSuccess(data);
      onClose();
      
      if (!editMode) {
        setFormData({
          title_pt: '',
          title_en: '',
          description_pt: '',
          description_en: '',
          thumbnail_url: '',
          order_index: 0,
          is_published: false
        });
      }
    } catch (err) {
      console.error(`❌ Erro ao ${editMode ? 'atualizar' : 'criar'} módulo:`, err);
      const errorMsg = err.response?.data?.details || err.response?.data?.error || `Erro ao ${editMode ? 'atualizar' : 'criar'} módulo`;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-background-800 border border-gray-700 rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-text-primary">
            {editMode ? 'Editar Módulo' : 'Criar Novo Módulo'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Título */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Título do Módulo
            </label>
            <input
              type="text"
              value={formData.title_pt}
              onChange={(e) => setFormData({...formData, title_pt: e.target.value})}
              className="w-full px-4 py-3 bg-background-900 border-2 border-gray-700 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all text-text-primary"
              placeholder="Ex: Marketing Digital do Zero"
              required
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Descrição
            </label>
            <textarea
              value={formData.description_pt}
              onChange={(e) => setFormData({...formData, description_pt: e.target.value})}
              className="w-full px-4 py-3 bg-background-900 border-2 border-gray-700 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all text-text-primary"
              rows="3"
              placeholder="Descreva o conteúdo do módulo..."
            />
          </div>

          {/* Thumbnail URL */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              URL da Thumbnail (opcional)
            </label>
            <input
              type="url"
              value={formData.thumbnail_url}
              onChange={(e) => setFormData({...formData, thumbnail_url: e.target.value})}
              className="w-full px-4 py-3 bg-background-900 border-2 border-gray-700 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all text-text-primary"
              placeholder="https://..."
            />
          </div>

          {/* Ordem e Publicado */}
          <div className="grid grid-cols-2 gap-4">
            <WheelPicker
              value={formData.order_index}
              onChange={(newValue) => setFormData({...formData, order_index: newValue})}
              min={1}
              max={50}
              label="Ordem de Exibição"
            />
            <div className="flex items-end">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
                  className="w-5 h-5 rounded border-gray-700 text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm font-semibold text-text-primary">
                  Publicar agora
                </span>
              </label>
            </div>
          </div>

          {/* Erro */}
          {error && (
            <div className="bg-red-500/10 border-2 border-red-500/50 text-red-400 px-4 py-3 rounded-2xl text-sm">
              {error}
            </div>
          )}

          {/* Botões */}
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-2xl font-semibold transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-2xl font-semibold transition-all disabled:opacity-50 shadow-lg shadow-primary-500/20"
            >
              {loading ? 'Salvando...' : editMode ? 'Atualizar Módulo' : 'Criar Módulo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
